<template>
  <div class="utility-calc">
    <h6 class="utility-calc-title">
      <feather-icon :icon="icon" size="14" class="mr-50" :class="`text-${variant}`" />
      {{ title }}
      <span v-if="periodLabel" class="text-muted font-weight-normal utility-calc-period">({{ periodLabel }})</span>
    </h6>

    <b-row>
      <b-col cols="6">
        <b-form-group class="mb-1">
          <label class="small mb-25">ยอดบิลรวม</label>
          <b-input-group append="฿" size="sm">
            <b-form-input
              :value="formatNumber(billAmount)"
              @input="v => billAmount = parseNumber(v)"
              type="text"
              placeholder="เช่น 18,413.30"
            />
          </b-input-group>
        </b-form-group>
      </b-col>

      <b-col cols="6">
        <b-form-group class="mb-1">
          <label class="small mb-25">หน่วยรวมตามบิล</label>
          <b-input-group :append="unitLabel" size="sm">
            <b-form-input
              :value="formatNumber(billUnits)"
              @input="v => billUnits = parseNumber(v)"
              type="text"
              placeholder="เช่น 3,798"
            />
          </b-input-group>
        </b-form-group>
      </b-col>
    </b-row>

    <b-form-group class="mb-1">
      <div class="d-flex justify-content-between align-items-baseline mb-25">
        <label class="small mb-0">อัตราเฉลี่ยต่อหน่วย</label>
        <b-link v-if="rateOverridden" class="small" @click="resetRate">
          <feather-icon icon="RotateCcwIcon" size="10" class="mr-25" />{{ billRate !== null ? 'ใช้ค่าเฉลี่ยจากบิล' : 'ใช้ค่ามาตรฐาน' }}
        </b-link>
      </div>
      <b-input-group :append="`บาท/${unitLabel}`" size="sm">
        <b-form-input
          :value="formatNumber(rate, 4)"
          @input="onRateInput"
          type="text"
          placeholder="เช่น 4.8482"
        />
      </b-input-group>
      <small v-if="!rateOverridden && billRate !== null" class="text-muted d-block mt-25">
        คำนวณจากบิล: {{ formatRate(billRate) }} บาท/{{ unitLabel }}
      </small>
      <small v-else-if="!rateOverridden && defaultRate" class="text-muted d-block mt-25">
        อัตรามาตรฐาน: {{ formatRate(defaultRate) }} บาท/{{ unitLabel }}
      </small>
    </b-form-group>

    <b-row>
      <b-col cols="6">
        <b-form-group class="mb-1">
          <label class="small mb-25">{{ startDateLabel }}</label>
          <b-form-datepicker v-model="periodStartDate" size="sm" locale="th" placeholder="เลือกวันที่" />
        </b-form-group>
      </b-col>

      <b-col cols="6">
        <b-form-group class="mb-1">
          <label class="small mb-25">{{ startReadingLabel }}</label>
          <b-form-input
            :value="formatNumber(periodStartReading)"
            @input="v => periodStartReading = parseNumber(v)"
            type="text"
            size="sm"
            placeholder="เช่น 162,044"
          />
        </b-form-group>
      </b-col>

      <b-col cols="6">
        <b-form-group class="mb-0">
          <label class="small mb-25">{{ endDateLabel }}</label>
          <b-form-datepicker v-model="periodEndDate" size="sm" locale="th" :min="periodStartDate" placeholder="เลือกวันที่" />
        </b-form-group>
      </b-col>

      <b-col cols="6">
        <b-form-group class="mb-0">
          <label class="small mb-25">{{ endReadingLabel }}</label>
          <b-form-input
            :value="formatNumber(periodEndReading)"
            @input="v => periodEndReading = parseNumber(v)"
            type="text"
            size="sm"
            placeholder="เช่น 163,917"
          />
        </b-form-group>
      </b-col>
    </b-row>

    <b-alert v-if="unitsUsed < 0" variant="warning" show class="p-50 mt-1 mb-0 small">
      {{ endReadingLabel }}น้อยกว่า{{ startReadingLabel }} กรุณาตรวจสอบตัวเลข
    </b-alert>

    <template v-else-if="hasResult">
      <hr class="utility-calc-divider" />

      <div class="utility-calc-result-row">
        <span class="text-muted">จำนวนหน่วยที่ใช้จริง</span>
        <strong>{{ formatNumber(unitsUsed) }} {{ unitLabel }}</strong>
      </div>

      <div class="utility-calc-total" :class="`bg-light-${variant}`">
        <span :class="`text-${variant}`">ยอดที่ต้องชำระ</span>
        <strong :class="`text-${variant}`">฿ {{ formatMoney(amountDue) }}</strong>
      </div>

      <small class="text-muted d-block mt-50">
        {{ formatNumber(unitsUsed) }} {{ unitLabel }} x {{ formatRate(rate) }} บาท = {{ formatMoney(amountDue) }} บาท
      </small>
    </template>
  </div>
</template>

<script>
import {
  BRow, BCol, BFormGroup, BFormInput, BInputGroup, BFormDatepicker, BAlert, BLink,
} from 'bootstrap-vue'

const THAI_MONTHS_SHORT = [
  'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
  'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.',
]

export default {
  name: 'UtilityCalculator',
  components: {
    BRow, BCol, BFormGroup, BFormInput, BInputGroup, BFormDatepicker, BAlert, BLink,
  },
  props: {
    title: { type: String, required: true },
    titleEn: { type: String, default: '' },
    icon: { type: String, default: 'ZapIcon' },
    unitLabel: { type: String, default: 'หน่วย' },
    unitLabelEn: { type: String, default: 'Units' },
    variant: { type: String, default: 'primary' },
    defaultRate: { type: Number, default: 0 },
    mode: { type: String, default: 'out' }, // 'out' = move-out, 'in' = move-in
  },
  data() {
    return {
      billAmount: null,
      billUnits: null,
      periodStartDate: null,
      periodStartReading: null,
      periodEndDate: null,
      periodEndReading: null,
      manualRate: null,
      rateOverridden: false,
    }
  },
  computed: {
    startDateLabel() {
      return this.mode === 'in' ? 'วันย้ายเข้า' : 'วันจดบิลล่าสุด'
    },
    startReadingLabel() {
      return this.mode === 'in' ? 'เลขมิเตอร์วันย้ายเข้า' : 'เลขมิเตอร์วันจดบิลล่าสุด'
    },
    endDateLabel() {
      return this.mode === 'in' ? 'วันจดบิลถัดไป' : 'วันย้ายออก'
    },
    endReadingLabel() {
      return this.mode === 'in' ? 'เลขมิเตอร์วันจดบิลถัดไป' : 'เลขมิเตอร์วันย้ายออก'
    },
    startReadingLabelEn() {
      return this.mode === 'in' ? 'Move-In Meter Reading' : 'Last Bill Meter Reading'
    },
    endReadingLabelEn() {
      return this.mode === 'in' ? 'Next Bill Meter Reading' : 'Move-Out Meter Reading'
    },
    billRate() {
      if (!this.billAmount || !this.billUnits) return null
      return this.billAmount / this.billUnits
    },
    rate() {
      if (this.rateOverridden && this.manualRate !== null) return this.manualRate
      if (this.billRate !== null) return this.billRate
      return this.defaultRate
    },
    unitsUsed() {
      if (this.periodEndReading === null || this.periodStartReading === null) return 0
      return this.periodEndReading - this.periodStartReading
    },
    amountDue() {
      return this.unitsUsed * this.rate
    },
    hasResult() {
      return this.rate > 0 && this.periodEndReading !== null && this.periodStartReading !== null
    },
    periodLabel() {
      if (!this.periodStartDate || !this.periodEndDate) return null
      return `${this.formatDateShort(this.periodStartDate)} - ${this.formatDateShort(this.periodEndDate)}`
    },
    summary() {
      return {
        title: this.title,
        titleEn: this.titleEn,
        unitLabel: this.unitLabel,
        unitLabelEn: this.unitLabelEn,
        mode: this.mode,
        billAmount: this.billAmount,
        billUnits: this.billUnits,
        rate: this.rate,
        startDateLabel: this.startDateLabel,
        startReadingLabel: this.startReadingLabel,
        startReadingLabelEn: this.startReadingLabelEn,
        endDateLabel: this.endDateLabel,
        endReadingLabel: this.endReadingLabel,
        endReadingLabelEn: this.endReadingLabelEn,
        periodStartDate: this.periodStartDate,
        periodStartReading: this.periodStartReading,
        periodEndDate: this.periodEndDate,
        periodEndReading: this.periodEndReading,
        unitsUsed: this.unitsUsed,
        amountDue: this.amountDue,
        hasResult: this.hasResult,
      }
    },
  },
  watch: {
    summary: {
      handler(val) {
        this.$emit('summary', val)
      },
      immediate: true,
    },
  },
  methods: {
    onRateInput(v) {
      this.manualRate = this.parseNumber(v)
      this.rateOverridden = true
    },
    resetRate() {
      this.rateOverridden = false
      this.manualRate = null
    },
    parseNumber(value) {
      if (!value) return null
      return Number(String(value).replace(/,/g, '').replace(/[^0-9.-]/g, ''))
    },
    formatNumber(value, maxDigits = 2) {
      if (value === null || value === undefined || Number.isNaN(value)) return ''
      return Number(value).toLocaleString(undefined, { maximumFractionDigits: maxDigits })
    },
    formatMoney(value) {
      if (value === null || value === undefined || Number.isNaN(value)) return ''
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    formatRate(value) {
      if (value === null || value === undefined || Number.isNaN(value)) return ''
      return Number(value).toLocaleString(undefined, { minimumFractionDigits: 4, maximumFractionDigits: 4 })
    },
    formatDateShort(isoDate) {
      const d = new Date(isoDate)
      return `${d.getDate()} ${THAI_MONTHS_SHORT[d.getMonth()]}`
    },
  },
}
</script>

<style scoped>
.utility-calc-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  font-weight: 600;
  font-size: 0.9rem;
  margin-bottom: 1rem;
}
.utility-calc-period {
  font-size: 0.8rem;
  margin-left: 6px;
}
.utility-calc-divider {
  margin: 0.75rem 0;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
}
.dark-layout .utility-calc-divider {
  border-top-color: rgba(255, 255, 255, 0.08);
}
.utility-calc-result-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-size: 0.85rem;
  padding: 0.25rem 0;
}
.utility-calc-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-radius: 0.357rem;
  padding: 0.6rem 0.9rem;
  margin-top: 0.4rem;
  font-weight: 600;
}
</style>
